import React from "react";
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

const BoardSelectionMenus = ({
  // Board selection hook state
  boards,
  loadingBoards,
  boardsError,
  newBoardDialogOpen,
  newBoardData,
  creatingBoard,
  createBoardError,
  boardMenuAnchor,
  columnMenuAnchor,
  selectedBoard,
  showGoToBoard,
  lastAddedBoard,
  boardMenuOpen,
  columnMenuOpen,

  // Board selection hook actions
  handleBoardMenuClose,
  handleBoardSelect,
  handleColumnMenuClose,
  handleCloseGoToBoard,
  handleNewBoardDialogOpen,
  handleNewBoardDialogClose,
  handleNewBoardInputChange,

  // Additional props
  athlete,
  onAddToBoard,
  onCreateBoard,
  onNavigateToBoard,
}) => {
  const handleAddToBoardClick = (boardId, columnId) => {
    if (onAddToBoard) {
      onAddToBoard(athlete, boardId, columnId);
    }
  };

  const handleCreateBoardClick = () => {
    if (onCreateBoard) {
      onCreateBoard(athlete);
    }
  };

  return (
    <>
      {/* Board selection menu */}
      <Menu
        id="board-menu"
        anchorEl={boardMenuAnchor}
        open={boardMenuOpen}
        onClose={handleBoardMenuClose}
        MenuListProps={{
          "aria-labelledby": "board-button",
        }}
      >
        {loadingBoards ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : boardsError ? (
          <MenuItem>
            <Typography color="error">{boardsError}</Typography>
          </MenuItem>
        ) : boards.length === 0 ? (
          <MenuItem>
            <Typography>No boards found</Typography>
          </MenuItem>
        ) : (
          boards.map((board) => (
            <MenuItem
              key={board.id || board._id}
              onClick={() => handleBoardSelect(board)}
            >
              <ListItemText
                primary={board.name}
                secondary={board.description}
              />
            </MenuItem>
          ))
        )}
        <Divider />
        <MenuItem onClick={handleNewBoardDialogOpen}>
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Create New Board</ListItemText>
        </MenuItem>
      </Menu>

      {/* Column selection menu */}
      <Menu
        id="column-menu"
        anchorEl={columnMenuAnchor}
        open={columnMenuOpen}
        onClose={handleColumnMenuClose}
        MenuListProps={{
          "aria-labelledby": "column-button",
        }}
      >
        {selectedBoard &&
          selectedBoard.columns &&
          selectedBoard.columns.map((column) => (
            <MenuItem
              key={column.id || column._id}
              onClick={() =>
                handleAddToBoardClick(
                  selectedBoard.id || selectedBoard._id,
                  column.id || column._id
                )
              }
            >
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  bgcolor: column.color || "#9e9e9e",
                  mr: 1,
                }}
              />
              <ListItemText>{column.title}</ListItemText>
            </MenuItem>
          ))}
      </Menu>

      {/* Take me to board option - appears after adding prospect */}
      {showGoToBoard && lastAddedBoard && (
        <>
          {/* Darkened background overlay */}
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: "rgba(0, 0, 0, 0.5)",
              backdropFilter: "blur(4px)",
              zIndex: 9999,
            }}
            onClick={handleCloseGoToBoard}
          />

          {/* Popup modal */}
          <Box
            sx={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 10000,
              bgcolor: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
              borderRadius: 2,
              boxShadow: 24,
              p: 3,
              border: 1,
              borderColor: "rgba(255, 255, 255, 0.2)",
              maxWidth: 400,
              width: "90%",
            }}
          >
            <Typography variant="h6" gutterBottom>
              Prospect Added Successfully!
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {lastAddedBoard.name} has been updated with the new prospect.
            </Typography>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button onClick={handleCloseGoToBoard} variant="outlined">
                Close
              </Button>
              <Button
                onClick={() => {
                  if (onNavigateToBoard) {
                    onNavigateToBoard(lastAddedBoard.id || lastAddedBoard._id);
                  }
                  handleCloseGoToBoard();
                }}
                variant="contained"
                color="primary"
              >
                Take me to this board
              </Button>
            </Box>
          </Box>
        </>
      )}

      {/* Create new board dialog */}
      <Dialog
        open={newBoardDialogOpen}
        onClose={handleNewBoardDialogClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Create New Board</DialogTitle>
        <DialogContent>
          {createBoardError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {createBoardError}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            id="board-name"
            name="name"
            label="Board Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newBoardData.name}
            onChange={handleNewBoardInputChange}
            required
            error={createBoardError && !newBoardData.name.trim()}
            helperText={
              createBoardError && !newBoardData.name.trim()
                ? "Board name is required"
                : ""
            }
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            id="board-description"
            name="description"
            label="Description (optional)"
            type="text"
            fullWidth
            variant="outlined"
            value={newBoardData.description}
            onChange={handleNewBoardInputChange}
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />
          <FormControl component="fieldset" sx={{ mt: 1 }}>
            <FormLabel component="legend">Template</FormLabel>
            <RadioGroup
              row
              name="template"
              value={newBoardData.template}
              onChange={handleNewBoardInputChange}
            >
              <FormControlLabel
                value="offerStatus"
                control={<Radio />}
                label="Offer Status"
              />
              <FormControlLabel
                value="blank"
                control={<Radio />}
                label="Blank Board"
              />
              <FormControlLabel
                value="fivePositions"
                control={<Radio />}
                label="5 Positions (RB, LB, QB, TE, WR)"
              />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleNewBoardDialogClose}>Cancel</Button>
          <Button
            onClick={handleCreateBoardClick}
            variant="contained"
            color="primary"
            disabled={creatingBoard}
          >
            {creatingBoard ? <CircularProgress size={24} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BoardSelectionMenus;
